import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Button, Form, InputGroup } from 'react-bootstrap';
import './UserManagement.css';
import api from '../services/api';
import useColumnWidths from '../components/useColumnWidths';
import ColumnWidthsReset from '../components/ColumnWidthsReset';

/** Sloupce výpisu uživatelů — klíče, pod kterými se ukládají šířky. */
const SLOUPCE = ['login', 'jmeno', 'prijmeni', 'aktivni', 'akce'];

function UserManagement({ openTab }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Šířky sloupců tažením — ukládají se uživateli do DB (migrace 048).
  const { wrapRef, colgroup, tableStyle, resizerProps, maUlozene, vychozi } =
    useColumnWidths('users.list', SLOUPCE);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('last_name');
  const [sortOrder, setSortOrder] = useState('asc');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getUsers();
      setUsers(Array.isArray(result.data) ? result.data : []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const handler = () => fetchUsers();
    window.addEventListener('users:refresh', handler);
    return () => window.removeEventListener('users:refresh', handler);
  }, [fetchUsers]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <i className="bi bi-chevron-expand text-muted ms-1" style={{ fontSize: '0.7rem' }}></i>;
    return sortOrder === 'asc'
      ? <i className="bi bi-chevron-up ms-1" style={{ fontSize: '0.7rem' }}></i>
      : <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.7rem' }}></i>;
  };

  const handleAddUser = () => {
    openTab('user-new', 'Nový uživatel', '/user/new', 'UserForm');
  };

  const handleOpenDetail = (user) => {
    const title = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
    openTab(`user-${user.id}`, title, `/user/${user.id}`, 'UserDetail', { userId: user.id });
  };

  const filtered = users
    .filter((u) => {
      if (statusFilter === 'active' && !u.is_active) return false;
      if (statusFilter === 'inactive' && u.is_active) return false;
      const s = searchTerm.trim().toLowerCase();
      if (!s) return true;
      return (
        (u.email || '').toLowerCase().includes(s) ||
        (u.first_name || '').toLowerCase().includes(s) ||
        (u.last_name || '').toLowerCase().includes(s)
      );
    })
    .sort((a, b) => {
      const av = (a[sortColumn] ?? '').toString().toLowerCase();
      const bv = (b[sortColumn] ?? '').toString().toLowerCase();
      if (av < bv) return sortOrder === 'asc' ? -1 : 1;
      if (av > bv) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <Container fluid className="px-4">
      <div className="header-bar mb-4">
        <Container fluid className="px-4">
          <div className="row align-items-center">
            <div className="col-md-3">
              <h4 className="mb-0">Uživatelé a role</h4>
            </div>
            <div className="col-md-9 text-end">
              <Button variant="primary" onClick={handleAddUser}>
                <i className="bi bi-plus-lg"></i> Přidat uživatele
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <div className="table-container">
        <div className="row g-3 align-items-end mb-4">
          <div className="col-md-5">
            <label className="form-label">Hledat</label>
            <InputGroup>
              <span className="input-group-text bg-white border-end-0">
                <i className="bi bi-search"></i>
              </span>
              <Form.Control
                className="border-start-0 search-box"
                placeholder="Login, jméno, příjmení…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>
          <div className="col-md-3">
            <label className="form-label">Stav</label>
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Vše</option>
              <option value="active">Aktivní</option>
              <option value="inactive">Neaktivní</option>
            </Form.Select>
          </div>
          <div className="col-md-4 text-end text-muted">
            Celkem: {filtered.length} / {users.length}
            <ColumnWidthsReset maUlozene={maUlozene} vychozi={vychozi} className="ms-2" />
          </div>
        </div>

        <div className="table-responsive grid-wrap" ref={wrapRef}>
          <Table hover className="align-middle grid-resizable" style={tableStyle}>
            {colgroup && (
              <colgroup>
                {colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
              </colgroup>
            )}
            <thead className="table-light">
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>
                  Login <SortIcon column="email" /><span {...resizerProps('login')} />
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('first_name')}>
                  Jméno <SortIcon column="first_name" /><span {...resizerProps('jmeno')} />
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('last_name')}>
                  Příjmení <SortIcon column="last_name" /><span {...resizerProps('prijmeni')} />
                </th>
                <th style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('is_active')}>
                  Aktivní <SortIcon column="is_active" /><span {...resizerProps('aktivni')} />
                </th>
                <th>Akce<span {...resizerProps('akce')} /></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">Načítám…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">Žádní uživatelé.</td>
                </tr>
              ) : (
                filtered.map((u) => (
                  <tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => handleOpenDetail(u)}>
                    <td>
                      <a
                        href="#"
                        className="text-decoration-none text-dark fw-semibold"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpenDetail(u); }}
                      >
                        {u.email || '—'}
                      </a>
                    </td>
                    <td>{u.first_name || '—'}</td>
                    <td>{u.last_name || '—'}</td>
                    <td className="text-center">
                      {u.is_active
                        ? <i className="bi bi-check-lg text-success" style={{ fontSize: '1.2rem' }} />
                        : <i className="bi bi-dash text-muted" />}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        title="Zobrazit detail"
                        onClick={() => handleOpenDetail(u)}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </Container>
  );
}

export default UserManagement;
